import { useRef } from 'react'
import Linked from 'shared-components/service/Linked'
import { discussionHref } from 'lib/community/discussions'
import {
  conversationStarterPending,
  pendingHeadline,
  pendingSentence,
  pointsFinePrint,
  pointsGuideRows,
  type PointsGuide,
  type StarterPending,
} from 'lib/community/gamification'
import type { CommunityStanding } from 'lib/community/types'
import { FloatingPanel, POPOVER_ZONE_ATTR, useDisclosureGroup } from './Popover'
import base from './community.module.css'
import badgeStyles from './Badges.module.css'
import styles from './Member.module.css'

const GUIDE_ID = 'points-guide'

export const PointsGuideBody: React.VFC<{ guide: PointsGuide; titleId: string }> = ({
  guide,
  titleId,
}) => (
  <>
    <p id={titleId} className={badgeStyles.popTitle}>
      {'How points work'}
    </p>
    <ul className={badgeStyles.popRows}>
      {pointsGuideRows(guide).map(row => (
        <li key={row.key} className={styles.guideRow}>
          <span className={badgeStyles.popRowLine}>
            <span className={badgeStyles.popLabel}>{row.label}</span>
            <b>{row.value}</b>
          </span>
          {row.note && <span className={styles.guideNote}>{row.note}</span>}
        </li>
      ))}
    </ul>
    <p className={badgeStyles.popText}>{pointsFinePrint(guide.points)}</p>
  </>
)

/**
 * "How points work" beside a member's points: every way to score, each value DealHunt's
 * (`pointsGuideRows`). Public — the summary's points table is the same for every reader, so the
 * button is in the shared HTML; the panel renders only while open (the badge popover's rule).
 */
export const HowPointsWork: React.VFC<{ guide: PointsGuide }> = ({ guide }) => {
  const anchor = useRef<HTMLSpanElement>(null)
  const { openId, triggerProps } = useDisclosureGroup()
  const open = openId === GUIDE_ID
  const { onMouseEnter, onMouseLeave, ...buttonProps } = triggerProps(GUIDE_ID)
  return (
    <span
      ref={anchor}
      className={styles.guideAnchor}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...{ [POPOVER_ZONE_ATTR]: '' }}
    >
      <button
        type="button"
        className={styles.guideButton}
        aria-expanded={open}
        aria-controls={open ? GUIDE_ID : undefined}
        {...buttonProps}
      >
        {'How points work'}
      </button>
      {open && (
        <FloatingPanel id={GUIDE_ID} labelledBy={`${GUIDE_ID}-title`} anchor={anchor}>
          <PointsGuideBody guide={guide} titleId={`${GUIDE_ID}-title`} />
        </FloatingPanel>
      )}
    </span>
  )
}

/**
 * "+20 on the way" under the points bar: the reader's own discussions that will earn the
 * Conversation Starter bonus once someone else replies (members saw "0 pts" after starting one,
 * with no word why). Pure — My gear mounts it from its own `/me` read (`StandingPointsOnTheWay`);
 * the member hub says the same thing inside Up next (`chooseUpNext`).
 */
export const PointsOnTheWay: React.VFC<{ pending: StarterPending; trailingSlash: boolean }> = ({
  pending,
  trailingSlash,
}) => {
  const [only] = pending.items
  const single = pending.total === 1 ? only : undefined
  return (
    <div className={styles.onTheWay}>
      <p className={styles.onTheWayLine}>
        <b>{pendingHeadline(pending)}</b>
        {' · '}
        {pendingSentence(pending)}
        {single && (
          <>
            {' '}
            <Linked href={discussionHref(single, trailingSlash)} className={base.cardLink}>
              {'Share it →'}
            </Linked>
          </>
        )}
      </p>
      {!single && pending.items.length > 0 && (
        <ul className={styles.onTheWayList}>
          {pending.items.map(item => (
            <li key={item.id}>
              <Linked href={discussionHref(item, trailingSlash)} className={styles.onTheWayLink}>
                {item.title}
              </Linked>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * The same line from the reader's own standing, for My gear (Guillaume 2026-10-05): a HIDDEN
 * profile has no member page (DealHunt 404s it), yet `/me` still sends it an eligible pending
 * block — My gear is the one place it can see "+N on the way". Same gate as the hub's Up next
 * (`conversationStarterPending`). Pure; My gear mounts it from its own `/me` read, client-side.
 */
export const StandingPointsOnTheWay: React.VFC<{
  standing: Pick<CommunityStanding, 'conversation_starter_pending'> | null | undefined
  trailingSlash: boolean
}> = ({ standing, trailingSlash }) => {
  const pending = conversationStarterPending(standing)
  return pending ? <PointsOnTheWay pending={pending} trailingSlash={trailingSlash} /> : null
}
